import "@/globals/styles.ts";
import "@/api/kili/kili-api.ts";
import { defaultConfig, plugin } from "@formkit/vue";
import * as Sentry from "@sentry/vue";
import { QueryClient, VueQueryPlugin } from "@tanstack/vue-query";
import * as AllRules from "@vee-validate/rules";
import FloatingVue from "floating-vue";
import { createPinia } from "pinia";
import { defineRule } from "vee-validate";
import { createApp } from "vue";
import VueApexCharts from "vue3-apexcharts";
import Vue3LottiePlugin from "vue3-lottie";

import { voltacallRequestContext } from "@/api/backoffice-v2/site/site-request-context";
import { parseMetaEnv } from "@/env";
import config from "@/formkit.config.ts";
import i18n from "@/i18n.ts";
import { filterExpectedErrors } from "@/lib/observability.ts";
import { queryClientConfig } from "@/lib/query-client.ts";
import { makeRouter } from "@/router.ts";
import { createServices, provideServicesPlugin } from "@/services";
import { mainSideEffects } from "@/side-effects";

import PrimeVue from "primevue/config";
import ConfirmationService from "primevue/confirmationservice";
import ToastService from "primevue/toastservice";

import App from "@/app.vue";

const env = parseMetaEnv(import.meta.env);
const services = createServices(env);

(window as any).global = window;
(window as any).process = {
  env: { DEBUG: undefined },
};

Object.keys(AllRules).forEach((rule) => {
  if (rule === "all") {
    return;
  }
  defineRule(rule, (AllRules as never)[rule]);
});

const pinia = createPinia();

export const app = createApp(App);
const queryClient = new QueryClient(queryClientConfig);
const router = makeRouter(i18n, queryClient);

app.use(provideServicesPlugin, { services });
app.use(VueQueryPlugin, { queryClient });
app.use(i18n);
app.use(router);
app.use(pinia);
app.use(PrimeVue);
app.use(plugin, defaultConfig(config));
app.use(VueApexCharts);
app.use(ToastService);
app.use(ConfirmationService);
app.use(Vue3LottiePlugin, { name: "LottieAnimation" });
app.use(FloatingVue);

Sentry.init({
  app,
  dsn: env.VITE_SENTRY_DSN,
  environment: __MODE__,
  enabled: env.VITE_SENTRY_ENABLED,
  release: __APP_VERSION__,
  integrations: [
    Sentry.browserTracingIntegration({
      router,
      shouldCreateSpanForRequest: (url) => /\/api\/site\/v2\/[^/?]+/.test(url),
    }),
  ],
  sampleRate: __IS_PRODUCTION__ ? 0.2 : 1,
  tracesSampleRate: __IS_PRODUCTION__ ? 0.2 : 1,
  replaysSessionSampleRate: 0,
  // Default (3) does not go deep enough for us.
  normalizeDepth: 8,
  beforeSend: filterExpectedErrors,
});

Sentry.setTags({
  "voltacall.tab.id": voltacallRequestContext.tabId,
  "voltacall.page_load.id": voltacallRequestContext.pageLoadId,
});

Sentry.setTags({
  "voltacall.tab.id": voltacallRequestContext.tabId,
  "voltacall.page_load.id": voltacallRequestContext.pageLoadId,
});

mainSideEffects();

app.mount("#app");
